import React from 'react';
import { Package, DollarSign, Lightbulb, Zap, CheckCircle, Tag, FileText } from 'lucide-react';
import MarkdownRenderer from '../../../components/common/MarkdownRenderer';
import EnhancedTableView from '../../../components/EnhancedTableView';
import BrandedPieChart from '../../../components/survey/BrandedPieChart';
import VisualizationRenderer from '../../../components/visualizations/VisualizationRenderer';
import { jsonrepair } from 'jsonrepair';

interface ContentData {
  hero_image?: { image: string; description?: string };
  headline?: string;
  consumer_insight?: string;
  main_benefit?: string;
  reasons_to_believe?: string[];
  price_point?: string | number;
  pack_size?: string | number;
  brand?: string;
  [key: string]: unknown;
}

interface TableData {
  type: string;
  title?: string;
  headers?: string[];
  data: any[];
}

// Structured output interfaces
interface PurchaseDecisionSummaryRow {
  verdict: string;
  count: number;
  percentage: number;
}

interface PrimaryFrictionCategoryRow {
  friction_category: string;
  count: number;
  percentage: number;
  description: string;
}

interface KeyRejectionReasonRow {
  rank: number;
  rejection_reason: string;
  description: string;
}

interface KeyPurchaseDriverRow {
  rank: number;
  purchase_driver: string;
  description: string;
}

interface CommonPurchaseConditionRow {
  rank: number;
  purchase_condition: string;
  description: string;
}

interface SummaryCardOutput {
  executive_summary: string;
  purchase_decision_summary: PurchaseDecisionSummaryRow[];
  primary_friction_categories: PrimaryFrictionCategoryRow[];
  key_rejection_reasons: KeyRejectionReasonRow[];
  key_purchase_drivers: KeyPurchaseDriverRow[];
  common_purchase_conditions: CommonPurchaseConditionRow[];
  key_insights: string[];
}

interface ParsedResponse {
  output: SummaryCardOutput | string;
  analysis: string;
  tables: TableData[];
}

interface ConceptTestResultsProps {
  simulationResponse: string;
  contentData: ContentData;
  isPreparingExport?: boolean;
  onImageClick?: (src: string, alt: string) => void;
}

const ConceptTestResults: React.FC<ConceptTestResultsProps> = ({
  simulationResponse,
  contentData,
  isPreparingExport = false,
  onImageClick,
}) => {
  // Parse simulation response
  const parseResponse = (): ParsedResponse => {
    try {
      const parsed = JSON.parse(jsonrepair(simulationResponse));
      return {
        output: parsed.output || '',
        analysis: parsed.analysis || '',
        tables: Array.isArray(parsed.tables) ? parsed.tables : [],
      };
    } catch (error) {
      console.error('Error parsing concept test response:', error);
      return { output: '', analysis: '', tables: [] };
    }
  };

  const parsedResponse = parseResponse();

  // Check if output is structured or string (for backward compatibility)
  const isStructuredOutput =
    typeof parsedResponse.output === 'object' && parsedResponse.output !== null;
  const structuredOutput = isStructuredOutput ? (parsedResponse.output as SummaryCardOutput) : null;

  // Extract product context from contentData
  const heroImage = contentData.hero_image;
  const headline = contentData.headline;
  const consumerInsight = contentData.consumer_insight;
  const mainBenefit = contentData.main_benefit;
  const reasonsToBelieve = contentData.reasons_to_believe || [];
  const pricePoint = contentData.price_point;
  const packSize = contentData.pack_size;
  const brand = contentData.brand;

  // Common styling
  const sectionCard = 'bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden';
  const sectionHeader =
    'flex items-center p-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100';
  const sectionTitle = 'text-base font-semibold text-gray-800';

  return (
    <div className="space-y-6">
      {/* Product Concept - Consolidated Section */}
      <div className={sectionCard}>
        <div className={sectionHeader}>
          <div className="p-2 bg-teal-100 rounded-lg mr-3">
            <Package className="h-5 w-5 text-teal-600" />
          </div>
          <h2 className={sectionTitle}>Product Concept</h2>
        </div>

        <div className="p-4">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Hero Image */}
            {heroImage?.image && (
              <div className="lg:w-1/3 flex-shrink-0">
                <div
                  className="relative bg-gray-50 rounded-lg border border-gray-200 p-3 cursor-pointer hover:border-teal-300 transition-colors"
                  onClick={() => onImageClick?.(heroImage.image, 'Product Hero Image')}
                >
                  <img
                    src={heroImage.image}
                    alt="Product Hero"
                    className="w-full h-48 object-contain rounded"
                  />
                </div>
                {heroImage.description && (
                  <p className="mt-2 text-xs text-gray-500 italic line-clamp-2">
                    {heroImage.description}
                  </p>
                )}
              </div>
            )}

            {/* Product Details - Compact Grid */}
            <div className="flex-1 space-y-3">
              {/* Top row: Brand, Price, Pack Size */}
              <div className="flex flex-wrap gap-2">
                {brand && (
                  <div className="flex items-center bg-indigo-50 rounded-lg px-3 py-1.5 border border-indigo-100">
                    <Tag className="h-3.5 w-3.5 text-indigo-600 mr-1.5" />
                    <span className="text-xs text-indigo-700 font-medium">{brand}</span>
                  </div>
                )}
                {pricePoint && (
                  <div className="flex items-center bg-green-50 rounded-lg px-3 py-1.5 border border-green-100">
                    <DollarSign className="h-3.5 w-3.5 text-green-600 mr-1" />
                    <span className="text-xs text-green-700 font-medium">{pricePoint}</span>
                  </div>
                )}
                {packSize && (
                  <div className="flex items-center bg-blue-50 rounded-lg px-3 py-1.5 border border-blue-100">
                    <Package className="h-3.5 w-3.5 text-blue-600 mr-1.5" />
                    <span className="text-xs text-blue-700 font-medium">{packSize}</span>
                  </div>
                )}
              </div>

              {/* Headline */}
              {headline && (
                <div>
                  <div className="flex items-center mb-1">
                    <FileText className="h-3.5 w-3.5 text-gray-500 mr-1" />
                    <span className="text-xs font-semibold text-gray-500 uppercase">Headline</span>
                  </div>
                  <p className="text-sm font-medium text-gray-900">{headline}</p>
                </div>
              )}

              {/* Consumer Insight */}
              {consumerInsight && (
                <div>
                  <div className="flex items-center mb-1">
                    <Lightbulb className="h-3.5 w-3.5 text-amber-500 mr-1" />
                    <span className="text-xs font-semibold text-gray-500 uppercase">
                      Consumer Insight
                    </span>
                  </div>
                  <p className="text-sm text-gray-700">{consumerInsight}</p>
                </div>
              )}

              {/* Main Benefit */}
              {mainBenefit && (
                <div>
                  <div className="flex items-center mb-1">
                    <Zap className="h-3.5 w-3.5 text-purple-500 mr-1" />
                    <span className="text-xs font-semibold text-gray-500 uppercase">
                      Main Benefit
                    </span>
                  </div>
                  <p className="text-sm text-gray-700">{mainBenefit}</p>
                </div>
              )}

              {/* Reasons to Believe */}
              {reasonsToBelieve.length > 0 && (
                <div>
                  <div className="flex items-center mb-1">
                    <CheckCircle className="h-3.5 w-3.5 text-green-500 mr-1" />
                    <span className="text-xs font-semibold text-gray-500 uppercase">
                      Reasons to Believe
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {reasonsToBelieve.map((rtb, index) => (
                      <span
                        key={index}
                        className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded border border-green-100"
                      >
                        {rtb}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Summary Card Output */}
      {parsedResponse.output && (
        <>
          {isStructuredOutput && structuredOutput ? (
            <>
              {/* Executive Summary */}
              {structuredOutput.executive_summary && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="w-2 h-2 bg-[#07E5D1] rounded-full"></div>
                    <h3 className="text-sm font-medium text-gray-900">Executive Summary</h3>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {structuredOutput.executive_summary}
                  </p>
                </div>
              )}

              {/* Purchase Decision Summary - Pie Chart */}
              {structuredOutput.purchase_decision_summary &&
                structuredOutput.purchase_decision_summary.length > 0 && (
                  <BrandedPieChart
                    data={structuredOutput.purchase_decision_summary.map((row) => ({
                      option: row.verdict,
                      count: row.count,
                      percentage: row.percentage,
                    }))}
                    title="Purchase Decision Summary"
                  />
                )}

              {/* Primary Friction Categories - Horizontal Bar Chart */}
              {structuredOutput.primary_friction_categories &&
                structuredOutput.primary_friction_categories.length > 0 && (
                  <VisualizationRenderer
                    visualization={{
                      type: 'bar_chart',
                      title: 'Primary Friction Categories',
                      data: structuredOutput.primary_friction_categories.map((item) => ({
                        category: item.friction_category,
                        name: item.friction_category,
                        value: item.count.toString(),
                        percentage: `${item.percentage}%`,
                        description: item.description,
                      })),
                    }}
                    isExporting={isPreparingExport}
                  />
                )}

              {/* Key Rejection Reasons */}
              {structuredOutput.key_rejection_reasons &&
                structuredOutput.key_rejection_reasons.length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <div className="w-2 h-2 bg-[#07E5D1] rounded-full"></div>
                      <h3 className="text-sm font-medium text-gray-900">Key Rejection Reasons</h3>
                    </div>
                    <div className="space-y-3">
                      {structuredOutput.key_rejection_reasons.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <span className="flex-shrink-0 w-6 h-6 bg-[#07E5D1] text-white text-xs font-medium rounded-full flex items-center justify-center">
                            {item.rank}
                          </span>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {item.rejection_reason}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">{item.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Key Purchase Drivers */}
              {structuredOutput.key_purchase_drivers &&
                structuredOutput.key_purchase_drivers.length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <div className="w-2 h-2 bg-[#07E5D1] rounded-full"></div>
                      <h3 className="text-sm font-medium text-gray-900">Key Purchase Drivers</h3>
                    </div>
                    <div className="space-y-3">
                      {structuredOutput.key_purchase_drivers.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <span className="flex-shrink-0 w-6 h-6 bg-[#07E5D1] text-white text-xs font-medium rounded-full flex items-center justify-center">
                            {item.rank}
                          </span>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {item.purchase_driver}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">{item.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Common Purchase Conditions */}
              {structuredOutput.common_purchase_conditions &&
                structuredOutput.common_purchase_conditions.length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center space-x-2 mb-4">
                      <div className="w-2 h-2 bg-[#07E5D1] rounded-full"></div>
                      <h3 className="text-sm font-medium text-gray-900">
                        Common Purchase Conditions
                      </h3>
                    </div>
                    <div className="space-y-3">
                      {structuredOutput.common_purchase_conditions.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <span className="flex-shrink-0 w-6 h-6 bg-[#07E5D1] text-white text-xs font-medium rounded-full flex items-center justify-center">
                            {item.rank}
                          </span>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {item.purchase_condition}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">{item.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Key Insights */}
              {structuredOutput.key_insights && structuredOutput.key_insights.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="w-2 h-2 bg-[#07E5D1] rounded-full"></div>
                    <h3 className="text-sm font-medium text-gray-900">Key Insights</h3>
                  </div>
                  <ul className="space-y-3">
                    {structuredOutput.key_insights.map((insight, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle className="h-4 w-4 text-[#07E5D1] mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{insight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : (
            /* Fallback to MarkdownRenderer for string output (backward compatibility) */
            <div className={sectionCard}>
              <div className="p-4">
                <MarkdownRenderer
                  content={parsedResponse.output as string}
                  variant="default"
                  bordered={false}
                  isExporting={isPreparingExport}
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* Verdicts Table */}
      {parsedResponse.tables.map(
        (table, index) =>
          table.type === 'table' &&
          table.headers &&
          table.data && (
            <div key={index} className={sectionCard}>
              <EnhancedTableView
                headers={table.headers}
                data={table.data}
                title={table.title}
                pdfMode={false}
                showExportButtons={true}
                showFullContent={true}
                images={[]}
                isExporting={isPreparingExport}
              />
            </div>
          ),
      )}
    </div>
  );
};

export default ConceptTestResults;
